/* Bespoke GLSL for the SupplyLens globe.
   Three distinct hand-written shader programs:
     1. Atmosphere — a back-rendered sphere shell with a fresnel rim so the
        horizon glows cyan and falls off toward the centre. No flat sprite.
     2. Risk column — a vertical energy pillar for incidents. Animated scan
        bands travel up the column; intensity/colour driven by severity.
     3. Ripple ring — an expanding flat ring that fades out, fired when a
        new high-severity incident appears.

   All uniforms are plain three.js values; the per-frame `uTime` is advanced
   from a single shared clock in customLayers.ts so every effect stays in sync.
*/

// --- 1. Atmosphere fresnel shell -----------------------------------------
// Rendered on a sphere slightly larger than the globe with BackSide so we see
// the inner surface; the fresnel term peaks at the silhouette = a rim halo.
export const ATMOSPHERE_VERT = /* glsl */ `
varying vec3 vNormal;
varying vec3 vViewDir;
void main() {
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  // Normal in view space; for a BackSide sphere we flip so it points at camera.
  vNormal = normalize(normalMatrix * normal);
  vViewDir = normalize(-mvPosition.xyz);
  gl_Position = projectionMatrix * mvPosition;
}
`

export const ATMOSPHERE_FRAG = /* glsl */ `
precision highp float;
uniform vec3  uColor;      // rim colour (cyan accent)
uniform vec3  uColorDeep;  // deeper blue toward the planet body
uniform float uIntensity;  // overall glow gain
uniform float uPower;      // fresnel falloff exponent
uniform float uTime;       // shared clock (s)
varying vec3 vNormal;
varying vec3 vViewDir;
void main() {
  // BackSide rendering flips the geometric normal away from the camera, so the
  // fresnel uses -vNormal. Rim (silhouette) → 1, centre → 0.
  float fres = pow(1.0 - abs(dot(normalize(vNormal), normalize(vViewDir))), uPower);
  // A very slow breathing pulse keeps the halo alive without being distracting.
  float breathe = 0.92 + 0.08 * sin(uTime * 0.6);
  vec3 col = mix(uColorDeep, uColor, fres);
  float alpha = clamp(fres * uIntensity * breathe, 0.0, 1.0);
  gl_FragColor = vec4(col, alpha);
}
`

// --- 2. Incident risk column ---------------------------------------------
// A unit cylinder (radius 1, height 1, origin at base) scaled in JS to the
// desired height. UV.y runs 0 (base) → 1 (top).
export const COLUMN_VERT = /* glsl */ `
varying vec2 vUv;
varying vec3 vViewNormal;
varying vec3 vViewDir;
void main() {
  vUv = uv;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vViewNormal = normalize(normalMatrix * normal);
  vViewDir = normalize(-mvPosition.xyz);
  gl_Position = projectionMatrix * mvPosition;
}
`

export const COLUMN_FRAG = /* glsl */ `
precision highp float;
uniform vec3  uColor;
uniform float uTime;
uniform float uSeed;     // per-incident phase offset so columns don't sync
uniform float uIntensity;
varying vec2 vUv;
varying vec3 vViewNormal;
varying vec3 vViewDir;
void main() {
  // Vertical fade: bright at the base, tapering toward the top.
  float vertical = pow(1.0 - vUv.y, 0.65);
  // Travelling scan band climbing the column.
  float band = sin((vUv.y * 9.0) - uTime * 3.0 + uSeed * 6.28318);
  band = smoothstep(0.55, 1.0, band) * 0.6;
  // Rim brightening on the cylinder silhouette so it reads as a glowing tube.
  float rim = pow(1.0 - abs(dot(normalize(vViewNormal), normalize(vViewDir))), 1.6);
  float glow = (vertical * 0.85 + band + rim * 0.5) * uIntensity;
  float alpha = clamp(glow, 0.0, 1.0) * (0.85 - vUv.y * 0.35);
  vec3 col = uColor + vec3(band * 0.4);
  gl_FragColor = vec4(col, alpha);
}
`

// --- 3. Ripple ring -------------------------------------------------------
// Drawn on a flat disc (PlaneGeometry, centred). uv centred at 0.5; the ring
// expands by reading a growing radius from uProgress (0→1) supplied per frame.
export const RIPPLE_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

export const RIPPLE_FRAG = /* glsl */ `
precision highp float;
uniform vec3  uColor;
uniform float uProgress;  // 0 (just spawned) → 1 (fully faded)
varying vec2 vUv;
void main() {
  vec2 p = vUv - 0.5;
  float r = length(p) * 2.0;            // 0 at centre → 1 at plane edge
  float ringR = mix(0.05, 1.0, uProgress);
  float width = 0.10 + uProgress * 0.06;
  // Distance from the travelling ring crest.
  float d = abs(r - ringR);
  float ring = smoothstep(width, 0.0, d);
  // Fade the whole thing out as it expands.
  float fade = (1.0 - uProgress);
  float alpha = ring * fade;
  // Discard the outer corners of the quad so we never show a hard square.
  if (r > 1.0) discard;
  gl_FragColor = vec4(uColor, alpha);
}
`
